import { type SiteSetupProcessDataStep } from "../../../types/api.types";
import { DEFAULT_STEP } from "../SiteSetupAssistant.constants";
import { getOrderFromStep } from "../utils/getOrderFromStep";
import { getStepFromOrder } from "../utils/getStepFromOrder";

interface UseStepNavigationProps {
  activeStep?: SiteSetupProcessDataStep;
  isSubmitting: boolean;
  isCreating: boolean;
  navigationBlocked: boolean;
  onStepChange: (step: SiteSetupProcessDataStep) => Promise<void> | void;
  totalSteps?: number;
}

interface UseStepNavigationReturn {
  previousStep: SiteSetupProcessDataStep | null;
  nextStep: SiteSetupProcessDataStep | null;
  handlePreviousStep: () => void;
  handleNextStep: () => void;
  isStepNavigationDisabled: (
    targetStep: SiteSetupProcessDataStep | null
  ) => boolean;
}

export function useStepNavigation({
  activeStep,
  isSubmitting,
  isCreating,
  navigationBlocked,
  onStepChange,
  totalSteps = 6
}: UseStepNavigationProps): UseStepNavigationReturn {
  const getPreviousStep = (): SiteSetupProcessDataStep | null => {
    const currentStepNumber = getOrderFromStep(activeStep || DEFAULT_STEP);
    if (currentStepNumber === null || currentStepNumber <= 0) {
      return null;
    }
    return getStepFromOrder(currentStepNumber - 1);
  };

  const previousStep = getPreviousStep();

  const getNextStep = (): SiteSetupProcessDataStep | null => {
    const currentStepNumber = getOrderFromStep(activeStep || DEFAULT_STEP);
    if (currentStepNumber === null || currentStepNumber >= totalSteps - 1) {
      return null;
    }
    return getStepFromOrder(currentStepNumber + 1);
  };

  const nextStep = getNextStep();

  const handlePreviousStep = () => {
    if (previousStep) {
      onStepChange(previousStep);
    }
  };

  const handleNextStep = () => {
    if (nextStep) {
      onStepChange(nextStep);
    }
  };

  const isStepNavigationDisabled = (
    targetStep: SiteSetupProcessDataStep | null
  ): boolean => {
    return (
      isSubmitting || isCreating || targetStep === null || navigationBlocked
    );
  };

  return {
    previousStep,
    nextStep,
    handlePreviousStep,
    handleNextStep,
    isStepNavigationDisabled
  };
}
